import { Box, Link, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useSearchYouTube from '../../../hooks/useSearchYoutube';
import useWindowSize from '../../../hooks/useWindowSize';
import { matchYouTubeToSpotify } from '../../../utils/youtubeSpotifyMatcher';
import { getArtistPath, getOptimizedImageUrl, trackClick } from '../../_helpers';
import { decodeEntities, displayTitle, formatDuration, spokenDuration } from '../../../utils/videoCatalog';

const YouTubeIcon = () => (
  <svg width="43" height="43" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M47.044 12.315C46.51 10.302 45.015 8.737 43.096 8.177C39.55 7.2 24 7.2 24 7.2C24 7.2 8.45 7.2 4.904 8.177C2.985 8.737 1.49 10.302 0.956 12.315C0 16.023 0 24 0 24C0 24 0 31.977 0.956 35.685C1.49 37.698 2.985 39.263 4.904 39.823C8.45 40.8 24 40.8 24 40.8C24 40.8 39.55 40.8 43.096 39.823C45.015 39.263 46.51 37.698 47.044 35.685C48 31.977 48 24 48 24C48 24 48 16.023 47.044 12.315Z"
      fill="var(--logoColor2)"
    />
    <path d="M19.2 31.2L31.68 24L19.2 16.8V31.2Z" fill="var(--color5)" />
  </svg>
);

export function VideoSection(props) {
  const { artistData, song } = props;
  const windowSize = useWindowSize();
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  // Get artist's top tracks for filtering
  const topTracks = artistData?.topTracks || [];
  const spotifyTracks = useMemo(() => {
    return topTracks.map((track: any) => ({
      name: track.name,
      artists: track.artists || [{ name: artistData?.name }],
    }));
  }, [topTracks, artistData?.name]);

  // Get the artist's latest videos - pass Spotify tracks for server-side filtering
  const {
    data: videos,
    isLoading,
    error,
  } = useSearchYouTube(
    {
      artistName: artistData?.name,
      songName: '',
      spotifyTracks,
    },
    { maxResults: 10 },
    [artistData?.name, spotifyTracks.length]
  );

  // Videos are already filtered by the server, but we can still rank them client-side
  const filteredVideos = useMemo(() => {
    if (!videos || videos.length === 0) return [];
    if (!artistData?.name) return videos;

    // Server already filters, but we can re-rank for display priority
    const rankedVideos = matchYouTubeToSpotify(videos, spotifyTracks, artistData.name);
    return rankedVideos;
  }, [videos, artistData?.name, spotifyTracks]);

  console.log('youtubeData:', {
    videosFromAPI: videos?.length,
    afterClientRanking: filteredVideos?.length,
    topVideos: filteredVideos
      ?.slice(0, 3)
      .map((v: any) => ({ title: v.snippet?.title, score: v.matchScore })),
    isLoading,
    error,
  });

  if (!artistData) {
    return null;
  }

  // Don't render if loading or no video data available
  if (isLoading) {
    return null;
  }

  // Use filtered videos if available, otherwise fall back to all videos
  const displayVideos = filteredVideos.length > 0 ? filteredVideos : videos || [];

  if (displayVideos.length === 0) {
    console.log('No YouTube videos found for artist');
    return null;
  }

  // Extract featured video data
  const featuredVideo = displayVideos[0];
  const featuredVideoId = featuredVideo?.id?.videoId;

  // If no valid video ID, don't render
  if (!featuredVideoId) {
    return null;
  }

  // Get other videos for the grid (up to 4)
  const gridVideos = displayVideos.slice(1, 5);

  const thumbnailUrl =
    featuredVideo?.snippet?.thumbnails?.high?.url ||
    featuredVideo?.snippet?.thumbnails?.medium?.url;
  const videoTitle = displayTitle(decodeEntities(featuredVideo?.snippet?.title), artistData?.name);
  const featuredDuration = formatDuration((featuredVideo as any)?.contentDetails?.duration || 0);
  const channelTitle = featuredVideo?.snippet?.channelTitle;
  const publishedAt = featuredVideo?.snippet?.publishedAt;

  const currentPlayingId = playingVideoId || (playingVideoId === null ? null : featuredVideoId);

  // Calculate days ago
  const getDaysAgo = (dateString) => {
    if (!dateString) return '';
    const publishDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - publishDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    if (diffDays < 30)
      return `${Math.floor(diffDays / 7)} ${Math.floor(diffDays / 7) === 1 ? 'week' : 'weeks'} ago`;
    if (diffDays < 365)
      return `${Math.floor(diffDays / 30)} ${Math.floor(diffDays / 30) === 1 ? 'month' : 'months'} ago`;
    return `${Math.floor(diffDays / 365)} ${Math.floor(diffDays / 365) === 1 ? 'year' : 'years'} ago`;
  };

  const handleVideoClick = (featuredVideoIdToPlay: string, source: 'featured' | 'grid' = 'featured', title?: string) => {
    trackClick(source === 'grid' ? 'video-grid-play' : 'video-featured-play', {
      label: title || featuredVideoIdToPlay,
      section: 'videos',
      platform: 'youtube',
      trackId: featuredVideoIdToPlay,
      href: `https://www.youtube.com/watch?v=${featuredVideoIdToPlay}`,
    });
    if (featuredVideoIdToPlay) {
      setPlayingVideoId(featuredVideoIdToPlay);
    }
  };

  const handleCloseVideo = () => {
    setPlayingVideoId(null);
  };

  const isPlaying = playingVideoId !== null;

  return (
    <Box
      className="VideosSection"
      sx={{
        position: 'relative',
        width: `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: { xs: '4rem 2rem', md: '3.5rem' },
        m: { xs: '1rem 2.5rem 0', md: '12.25rem 2.5rem 0' },
        backgroundColor: 'var(--color5)',
        borderRadius: '1.875rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: '3rem', md: '3rem' },
        zIndex: 1,
      }}
    >
      <Box
        className="VideosHeader"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: { xs: '3rem', md: '2.5rem' },
          flexWrap: 'wrap',
        }}
      >
        <Typography
          className="Videos"
          sx={{
            color: 'var(--logoColor2)',
            fontSize: { xs: '3rem', md: '3.34rem' },
            fontFamily: 'Hanson',
            fontWeight: '700',
          }}
        >
          VIDEOS
        </Typography>
        <Box
          className="YoutubeLogoAndUsername"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '0.75rem', md: '0.75rem' },
          }}
        >
          <YouTubeIcon />
          <Typography
            className="ChannelName"
            sx={{
              color: 'var(--logoColor2)',
              fontSize: { xs: '1.25rem', md: '1.25rem' },
              fontFamily: 'Poppins',
              fontWeight: '700',
            }}
          >
            {channelTitle || artistData.name}
          </Typography>
        </Box>
      </Box>

      <Box
        className="VideoContent"
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '2.5rem', md: '2.5rem' },
          alignItems: { xs: 'center', md: 'flex-start' },
        }}
      >
        {/* Featured Video */}
        <Box
          className="FeaturedVideo"
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 65%' },
            maxWidth: { xs: '100%', md: '65%' },
            position: 'relative',
            cursor: featuredVideoId && !isPlaying ? 'pointer' : 'default',
          }}
          onClick={!isPlaying ? () => handleVideoClick(featuredVideoId, 'featured', videoTitle) : undefined}
          onKeyDown={!isPlaying ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleVideoClick(featuredVideoId, 'featured', videoTitle);
            }
          } : undefined}
          role={!isPlaying ? 'button' : undefined}
          tabIndex={!isPlaying ? 0 : undefined}
          aria-label={!isPlaying ? `Play ${videoTitle || 'featured video'}` : undefined}
        >
          {isPlaying && playingVideoId ? (
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${playingVideoId}?autoplay=1&rel=0`}
                title={videoTitle || 'Video player'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: { xs: '1.5rem', md: '0.73rem' },
                }}
              />
              <Box
                component="button"
                type="button"
                aria-label="Close video"
                onClick={() => {
                  trackClick('video-close', { label: 'Close video', section: 'videos' });
                  handleCloseVideo();
                }}
                sx={{
                  border: 0,
                  minWidth: '44px',
                  minHeight: '44px',
                  position: 'absolute',
                  top: { xs: '-1.5rem', md: '-0.75rem' },
                  right: { xs: '-0.5rem', md: '-0.75rem' },
                  width: { xs: '2.5rem', md: '2rem' },
                  height: { xs: '2.5rem', md: '2rem' },
                  borderRadius: '50%',
                  backgroundColor: 'var(--logoColor2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'var(--logoColor)',
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color5)',
                    fontSize: { xs: '1.25rem', md: '1rem' },
                    fontWeight: '700',
                    lineHeight: 1,
                  }}
                >
                  ×
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                component="img"
                src={
                  getOptimizedImageUrl(thumbnailUrl, { width: 640, quality: 80 }) ||
                  'https://via.placeholder.com/640x360'
                }
                alt={videoTitle || 'Video thumbnail'}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '16/9',
                  borderRadius: { xs: '1.5rem', md: '0.73rem' },
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: featuredVideoId ? 'scale(1.02)' : 'none',
                  },
                }}
              />

              {/* Play Button Overlay */}
              {featuredVideoId && (
                <Box
                  className="PlayButton"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '6rem', md: '4rem' },
                    height: { xs: '6rem', md: '4rem' },
                    borderRadius: '50%',
                    backgroundColor: 'var(--logoColor2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translate(-50%, -50%) scale(1.1)',
                      backgroundColor: 'var(--logoColor)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: 'var(--color5)',
                      fontSize: { xs: '1.5rem', md: '0.875rem' },
                      fontFamily: 'Hanson',
                      fontWeight: '700',
                    }}
                  >
                    PLAY
                  </Typography>
                </Box>
              )}

              {/* Video Metadata */}
              {videoTitle && (
                <Box
                  className="VideoMetaData"
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '2rem', md: '1rem' },
                    left: { xs: '1.5rem', md: '0.75rem' },
                    right: { xs: '1.5rem', md: '0.75rem' },
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: { xs: '1.2rem', md: '0.73rem' },
                    padding: { xs: '1.5rem', md: '0.75rem' },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: { xs: '1rem', md: '0.5rem' },
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontSize: { xs: '1.25rem', md: '1rem' },
                      fontFamily: 'Poppins',
                      fontWeight: '400',
                      flex: '1 1 auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {videoTitle}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: '0.75rem', md: '0.75rem' },
                    }}
                  >
                    {publishedAt && (
                      <Typography
                        sx={{
                          color: '#ffffff',
                          fontSize: { xs: '1.125rem', md: '1rem' },
                          fontFamily: 'Poppins',
                          fontWeight: '400',
                        }}
                      >
                        {[getDaysAgo(publishedAt), featuredDuration].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Discover More Videos */}
        <Box
          className="DiscoverVideoGroup"
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 35%' },
            maxWidth: { xs: '100%', md: '35%' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '2rem', md: '1.25rem' },
          }}
        >
          <Box
            className="DiscoverHeader"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: '1.5rem', md: '0.75rem' },
            }}
          >
            <Typography
              className="Discover"
              sx={{
                color: 'var(--logoColor2)',
                fontSize: { xs: '18px', md: '1.67rem' },
                fontFamily: 'Monument Extended',
                fontWeight: '700',
                letterSpacing: '0.078rem',
              }}
            >
              More Videos
            </Typography>
            <Link
              to={getArtistPath(artistData.name, '/videos')}
              component={RouterLink}
              sx={{
                color: 'var(--logoColor)',
                fontSize: { xs: '14px', md: 'clamp(13px, 0.875rem, 15px)' },
                fontFamily: 'Poppins',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '44px',
                '&:hover': { color: 'var(--logoColor2)' },
              }}
            >
              View All →
            </Link>
          </Box>

          {/* 2x2 Video Grid */}
          {gridVideos.length > 0 && (
            <Box
              className="VideoGrid"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: { xs: '1rem', md: '0.75rem' },
              }}
            >
              {gridVideos.map((gridVideo: any) => {
                const gridVideoId = gridVideo?.id?.videoId;
                const gridThumbnail =
                  gridVideo?.snippet?.thumbnails?.medium?.url ||
                  gridVideo?.snippet?.thumbnails?.default?.url;
                const gridTitle = displayTitle(decodeEntities(gridVideo?.snippet?.title), artistData?.name);
                const gridSeconds = gridVideo?.contentDetails?.duration || 0;
                const gridDuration = formatDuration(gridSeconds);
                const gridSpoken = spokenDuration(gridSeconds);

                if (!gridVideoId) return null;

                return (
                  <Box
                    key={gridVideoId}
                    component="button"
                    type="button"
                    aria-label={`Play ${gridTitle}${gridSpoken ? `, ${gridSpoken}` : ''}`}
                    onClick={() => handleVideoClick(gridVideoId, 'grid', gridTitle)}
                    sx={{
                      position: 'relative',
                      display: 'block',
                      width: '100%',
                      p: 0,
                      border: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      '&:focus-visible': { outline: '2px solid var(--logoColor2)', outlineOffset: '3px' },
                      '&:focus-visible .PlayOverlay': { opacity: 1 },
                      '@media (hover: none)': { '& .PlayOverlay': { opacity: 1, backgroundColor: 'rgba(0,0,0,0.18)' } },
                      borderRadius: { xs: '0.75rem', md: '0.5rem' },
                      overflow: 'hidden',
                      aspectRatio: '16/9',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      },
                      '&:hover .PlayOverlay': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={getOptimizedImageUrl(gridThumbnail, { width: 320, quality: 75 })}
                      alt={gridTitle}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    {gridDuration ? (
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          right: '6px',
                          bottom: '6px',
                          zIndex: 1,
                          px: '5px',
                          borderRadius: '4px',
                          backgroundColor: 'rgba(8,8,8,0.8)',
                          color: '#fff',
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '11px',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {gridDuration}
                      </Box>
                    ) : null}
                    <Box
                      className="PlayOverlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '3rem', md: '2rem' },
                          height: { xs: '3rem', md: '2rem' },
                          borderRadius: '50%',
                          backgroundColor: 'var(--logoColor2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'var(--color5)',
                            fontSize: { xs: '0.625rem', md: '0.5rem' },
                            fontFamily: 'Hanson',
                            fontWeight: '700',
                          }}
                        >
                          ▶
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {featuredVideoId && (
            <Link
              href={`https://www.youtube.com/channel/${(featuredVideo.snippet as any)?.channelId || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-block',
                padding: { xs: '1.25rem 2rem', md: '0.875rem 1.75rem' },
                backgroundColor: 'var(--logoColor2)',
                color: 'var(--color5)',
                borderRadius: { xs: '0.5rem', md: '0.5rem' },
                fontSize: { xs: '2rem', md: '1rem' },
                fontFamily: 'Hanson',
                fontWeight: '700',
                textDecoration: 'none',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                letterSpacing: '0.075rem',
                '&:hover': {
                  backgroundColor: 'var(--logoColor)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              VIEW CHANNEL
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
}
